import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import timezoneData from '@vacationtracker/shared/data/timezone'
import { isLocationNameTaken, missingKind, placeLabel, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import { getFormattedWorkhours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { getAverageWorkingHoursForLocation } from '@vacationtracker/shared/functions/work-week'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

import { Button } from '../../../../../../design-system/Button'
import { RecordCardMissing } from '../../../../../../design-system/RecordCard'
import { OwnWorkWeekPopover } from './OwnWorkWeekPopover'
import { EditableRow } from '../../../../../../components/inline-edit/EditableRow'
import { useInlineEditGroup } from '../../../../../../components/inline-edit/context'
import { workWeekAsText } from '../../../../../../components/work-week'
import IntlMessages from '../../../../../../util/IntlMessages'
import { resetQuotas as formatResetQuotas } from '../../../../../../util/reset-quotas-based-on'
import { publicHolidaysForPlace } from '../../../../../../util/location-facts'
import { IGetLocationGeneral, IGetLocationList } from '../../../../../../types/locations'
import { IPlaceValue, LeaveYearEditor, PlaceEditor, SelectRowEditor, TextRowEditor, ToggleRowEditor, WorkdayEditor, WorkWeekEditor } from './editors'
import { useLocationFieldSave } from './use-location-field-save'

export interface ILocationGeneralProps {
  location: IGetLocationGeneral
  /** Every other location, for the duplicate-name check. */
  otherLocations: IGetLocationList[]
  locale: LocaleEnum
  canEdit: boolean
  /** i18n id shown on every row when this admin does not manage the location. */
  lockReason?: string
  workdayEnabled: boolean
  onPatch: (patch: Partial<IGetLocationGeneral>) => void
}

/** Matches the cap the create form has always applied to a location name. */
const LOCATION_NAME_MAX_LENGTH = 100

const LocationGeneral = (props: ILocationGeneralProps): React.ReactElement => {
  const { location, otherLocations, locale, canEdit, lockReason, workdayEnabled, onPatch } = props
  const { formatMessage } = useIntl()
  const { saveFields, saveWorkWeek, importHolidays } = useLocationFieldSave({ location, onPatch })

  const [isBlocked, setIsBlocked] = useState(false)

  const rowLock = canEdit ? undefined : lockReason
  const countryCode = location.countryCode ?? ''
  const place = { countryCode, subdivisionCode: location.subdivisionCode ?? null }
  const timezoneOption = timezoneData.find((item) => item.id === location.timezone)

  // Which row was refused does not need keeping: the warning names none of them.
  const onBlocked = () => setIsBlocked(true)

  // The warning is about one specific attempt; once the blocking edit is resolved it is stale.
  const { openKey } = useInlineEditGroup()
  useEffect(() => setIsBlocked(false), [openKey])

  // Only the open row renders this, and only one row is ever open, so handing it to all of them
  // costs nothing and keeps the message on the edit it is actually about.
  const rowProps = (labelId: string) => ({
    labelId,
    label: <IntlMessages id={labelId} />,
    lockReason: rowLock,
    onBlocked,
    blockedMessage: isBlocked ? formatMessage({ id: 'locationGeneral.unsavedBlocked' }) : undefined,
  })

  const commitPlace = async (value: IPlaceValue) => {
    await saveFields({ countryCode: value.countryCode, subdivisionCode: value.subdivisionCode })
    if (!value.importHolidays || !value.countryCode) {
      return
    }
    const place = { countryCode: value.countryCode, subdivisionCode: value.subdivisionCode }
    const offer = publicHolidaysForPlace(place, locale)
    if (offer) {
      await importHolidays(place, offer, locale)
    }
  }

  // missingKind only reports a subdivision for countries that actually have them.
  const missingSubdivision = missingKind(place) === 'subdivision'

  // Shared by both work-week rows: each edits one half and sends the other back unchanged.
  const workWeekValue = { workWeek: location.workWeek || [], hoursPerDay: getAverageWorkingHoursForLocation(location.workHours) }
  // Who a change to either row can reach: everyone else already follows the location.
  const ownWorkWeekUsers = (location.users || []).filter((user) => user.workWeekType === 'USER')
  const customWorkWeekCount = ownWorkWeekUsers.length

  return (
    <div>
      <EditableRow<string>
        {...rowProps('app.name')}
        display={location.name}
        renderEditor={(actions) => (
          <TextRowEditor ariaLabel={formatMessage({ id: 'app.name' })} required value={location.name} maxLength={LOCATION_NAME_MAX_LENGTH} actions={actions} />
        )}
        onCommit={async (name) => {
          const trimmed = name.trim()
          // The event's Joi schema requires a non-empty name, so without this an empty field is a
          // 400 several seconds later rather than a message on the row.
          if (!trimmed) {
            throw new Error(formatMessage({ id: 'form.inputRequired' }))
          }
          const takenNames = otherLocations.filter((item) => item.id !== location.id).map((item) => item.name)
          // The backend rejects duplicates asynchronously, so checking here is the difference
          // between an inline error and a toast several seconds later.
          if (isLocationNameTaken(trimmed, takenNames)) {
            throw new Error(formatMessage({ id: 'error.CannotDuplicateName' }))
          }
          await saveFields({ name: trimmed })
        }}
      />

      <EditableRow<IPlaceValue>
        {...rowProps('locationGeneral.label.place')}
        display={
          countryCode ? (
            placeLabel(place)
          ) : (
            // A location with no country imports no holidays, so this is a gap to fix rather than a
            // blank - the same marker the locations list puts on the card.
            <RecordCardMissing>
              <IntlMessages id="app.notSet" />
            </RecordCardMissing>
          )
        }
        note={
          countryCode && missingSubdivision ? (
            <IntlMessages id="locationPlace.card.termNotSet" values={{ term: formatMessage({ id: subdivisionTermKey(countryCode) }) }} />
          ) : undefined
        }
        renderEditor={(actions) => (
          <PlaceEditor
            value={{ countryCode: location.countryCode ?? null, subdivisionCode: location.subdivisionCode ?? null, importHolidays: false }}
            actions={actions}
            locale={locale}
          />
        )}
        onCommit={commitPlace}
      />

      <EditableRow<number>
        {...rowProps('locationGeneral.label.weekStartsOn')}
        display={<IntlMessages id={location.firstDayOfWeek === 1 ? 'app.monday' : 'app.sunday'} />}
        renderEditor={(actions) => (
          // SegmentedToggle's value is string | boolean, so the day number converts at this
          // boundary rather than the shared component widening for one caller.
          <ToggleRowEditor
            value={String(location.firstDayOfWeek)}
            options={[
              { value: '1', label: formatMessage({ id: 'app.monday' }) },
              { value: '0', label: formatMessage({ id: 'app.sunday' }) },
            ]}
            actions={{ ...actions, setDraft: (next) => actions.setDraft(Number(next)) }}
          />
        )}
        onCommit={async (value) => {
          await saveFields({ firstDayOfWeek: value })
        }}
      />

      <EditableRow<Parameters<typeof saveWorkWeek>[0]>
        {...rowProps('locationGeneral.label.workWeek')}
        display={workWeekAsText(
          {
            days: [...(location.workWeek || [])],
            firstDayOfWeek: location.firstDayOfWeek,
            withAnd: formatMessage({ id: 'app.and' }),
          },
          formatMessage
        )}
        // Says up front that this row does not speak for everyone, and opens the list of who.
        note={
          customWorkWeekCount > 0 ? (
            <OwnWorkWeekPopover users={ownWorkWeekUsers} firstDayOfWeek={location.firstDayOfWeek}>
              {/* The row itself opens the editor on click, and this sits inside it. The popover
                  adds its own handler to this same span, so both still run. */}
              <span onClick={(event) => event.stopPropagation()}>
                {/* Text, not link: it reads as an aside on the row rather than a primary action,
                    so it stays the body colour and carries an underline instead. */}
                <Button variant="text" size="small" style={{ textDecoration: 'underline' }}>
                  <IntlMessages id="locationGeneral.ownWorkWeekCount" values={{ count: customWorkWeekCount }} />
                </Button>
              </span>
            </OwnWorkWeekPopover>
          ) : undefined
        }
        renderEditor={(actions) => <WorkWeekEditor value={workWeekValue} firstDayOfWeek={location.firstDayOfWeek} customCount={customWorkWeekCount} actions={actions} />}
        onCommit={saveWorkWeek}
      />

      {/* Only meaningful when leave is counted in hours, which is what the row's own hint says. */}
      {workdayEnabled && (
        <EditableRow<Parameters<typeof saveWorkWeek>[0]>
          {...rowProps('locationGeneral.label.workday')}
          display={getFormattedWorkhours(
            formatMessage,
            location.workHours?.map((pair) => [...pair]),
            location.firstDayOfWeek
          )}
          renderEditor={(actions) => <WorkdayEditor value={workWeekValue} customCount={customWorkWeekCount} actions={actions} />}
          onCommit={saveWorkWeek}
        />
      )}

      <EditableRow<string>
        {...rowProps('location.timezone')}
        display={timezoneOption ? timezoneOption.text : formatMessage({ id: 'location.timezoneDefault' })}
        renderEditor={(actions) => (
          <SelectRowEditor
            ariaLabel={formatMessage({ id: 'location.timezone' })}
            value={location.timezone}
            options={timezoneData.map((item) => ({ value: item.id, label: item.text }))}
            actions={actions}
          />
        )}
        onCommit={async (timezone) => {
          await saveFields({ timezone })
        }}
      />

      <EditableRow<{ resetQuotas: string; yearStartMonth: number; yearStartDay: number }>
        {...rowProps('locationGeneral.label.resetQuotas')}
        display={formatResetQuotas(
          {
            status: location.resetQuotas,
            yearStartDay: location.yearStartDay,
            yearStartMonth: location.yearStartMonth,
            locale,
            locationTimezone: location.timezone,
          },
          formatMessage
        )}
        renderEditor={(actions) => (
          <LeaveYearEditor
            value={{
              resetQuotas: location.resetQuotas,
              yearStartMonth: location.yearStartMonth,
              yearStartDay: location.yearStartDay,
            }}
            actions={actions}
          />
        )}
        onCommit={async (value) => {
          await saveFields(value)
        }}
      />
    </div>
  )
}

export default LocationGeneral
